import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
} from "react";
import { UPLOAD_STATUS } from "constants/S3Constant";

const UploadContext = createContext();

const initialState = {
  uploads: [],
  isUploading: false,
  isGeneratingUrls: false,
  totalFiles: 0,
  completedFiles: 0,
  failedFiles: 0,
  cancelledFiles: 0,
  isMinimized: false,
  isCancelled: false,
  uploadTaskId: null,
  position: { x: window.innerWidth - 420, y: 100 },
  activeXhrRequests: new Map(),
  cancelToken: null, // Add cancel token
};

const uploadReducer = (state, action) => {
  switch (action.type) {
    case "SET_GENERATING_URLS":
      return {
        ...state,
        isGeneratingUrls: action.payload,
      };

    case "START_UPLOAD":
      // Create new cancel token
      const cancelToken = { isCancelled: false };

      return {
        ...state,
        uploads: action.payload.uploads,
        isUploading: true,
        isCancelled: false,
        isGeneratingUrls: false,
        totalFiles: action.payload.totalFiles,
        completedFiles: 0,
        failedFiles: 0,
        cancelledFiles: 0,
        isMinimized: false,
        uploadTaskId: action.payload.taskId,
        activeXhrRequests: new Map(),
        cancelToken,
      };

    case "ADD_XHR_REQUEST":
      const newXhrMap = new Map(state.activeXhrRequests);
      newXhrMap.set(action.payload.taskId, action.payload.xhr);
      return {
        ...state,
        activeXhrRequests: newXhrMap,
      };

    case "REMOVE_XHR_REQUEST":
      const updatedXhrMap = new Map(state.activeXhrRequests);
      updatedXhrMap.delete(action.payload.taskId);
      return {
        ...state,
        activeXhrRequests: updatedXhrMap,
      };

    case "UPDATE_UPLOAD_PROGRESS":
      // Don't update progress if cancelled
      if (state.isCancelled) return state;

      return {
        ...state,
        uploads: state.uploads.map((upload) =>
          upload.id === action.payload.id
            ? { ...upload, progress: action.payload.progress }
            : upload
        ),
      };

    case "UPDATE_UPLOAD_STATUS":
      // Don't update status if cancelled (except to cancelled status)
      if (
        state.isCancelled &&
        action.payload.status !== UPLOAD_STATUS.CANCELLED
      ) {
        return state;
      }

      const updatedUploads = state.uploads.map((upload) =>
        upload.id === action.payload.id
          ? {
              ...upload,
              status: action.payload.status,
              error: action.payload.error,
            }
          : upload
      );

      const completed = updatedUploads.filter(
        (u) => u.status === UPLOAD_STATUS.SUCCESS
      ).length;
      const failed = updatedUploads.filter(
        (u) => u.status === UPLOAD_STATUS.FAILED
      ).length;
      const cancelled = updatedUploads.filter(
        (u) => u.status === UPLOAD_STATUS.CANCELLED
      ).length;

      return {
        ...state,
        uploads: updatedUploads,
        completedFiles: completed,
        failedFiles: failed,
        cancelledFiles: cancelled,
        isUploading:
          !state.isCancelled &&
          completed + failed + cancelled < state.totalFiles,
      };

    case "CANCEL_UPLOAD":
      console.log("CANCEL_UPLOAD action triggered");

      // Set cancel token to stop all uploads immediately
      if (state.cancelToken) {
        state.cancelToken.isCancelled = true;
      }

      // Force abort ALL active XHR requests
      console.log("Aborting XHR requests:", state.activeXhrRequests.size);
      state.activeXhrRequests.forEach((xhr, taskId) => {
        try {
          console.log(`Aborting XHR for task: ${taskId}`);
          if (xhr && xhr.readyState !== XMLHttpRequest.DONE) {
            xhr.abort();
          }
        } catch (error) {
          console.error("Error aborting XHR:", error);
        }
      });

      // Mark ALL pending and uploading items as cancelled immediately
      const cancelledUploads = state.uploads.map((upload) => {
        if (
          upload.status === UPLOAD_STATUS.PENDING ||
          upload.status === UPLOAD_STATUS.UPLOADING
        ) {
          return { ...upload, status: UPLOAD_STATUS.CANCELLED, progress: 0 };
        }
        return upload;
      });

      const cancelledCount = cancelledUploads.filter(
        (u) => u.status === UPLOAD_STATUS.CANCELLED
      ).length;

      console.log(`Cancelled ${cancelledCount} uploads`);

      return {
        ...state,
        isUploading: false,
        isCancelled: true,
        activeXhrRequests: new Map(), // Clear all XHR references
        uploads: cancelledUploads,
        cancelledFiles: cancelledCount,
      };

    case "RETRY_UPLOAD":
      // Reset cancel token for retry
      const newCancelToken = { isCancelled: false };

      return {
        ...state,
        uploads: state.uploads.map((upload) =>
          action.payload.ids.includes(upload.id)
            ? {
                ...upload,
                status: UPLOAD_STATUS.PENDING,
                error: null,
                progress: 0,
              }
            : upload
        ),
        isUploading: true,
        isCancelled: false,
        activeXhrRequests: new Map(),
        cancelToken: newCancelToken,
      };

    case "TOGGLE_MINIMIZE":
      return {
        ...state,
        isMinimized: !state.isMinimized,
      };

    case "UPDATE_POSITION":
      return {
        ...state,
        position: action.payload.position,
      };

    case "CLEAR_UPLOADS":
      // Set cancel token and abort any remaining XHR requests
      if (state.cancelToken) {
        state.cancelToken.isCancelled = true;
      }

      state.activeXhrRequests.forEach((xhr, taskId) => {
        try {
          if (xhr && xhr.readyState !== XMLHttpRequest.DONE) {
            xhr.abort();
          }
        } catch (error) {
          console.error("Error aborting XHR:", error);
        }
      });

      return {
        ...initialState,
        position: state.position,
      };

    default:
      return state;
  }
};

export const UploadProvider = ({ children }) => {
  const [state, dispatch] = useReducer(uploadReducer, initialState);

  // Add beforeunload event listener for page refresh warning
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (
        (state.isUploading || state.isGeneratingUrls) &&
        state.totalFiles > 0
      ) {
        const message =
          "You have uploads in progress. Are you sure you want to leave?";
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [state.isUploading, state.isGeneratingUrls, state.totalFiles]);

  const setGeneratingUrls = useCallback((isGenerating) => {
    dispatch({
      type: "SET_GENERATING_URLS",
      payload: isGenerating,
    });
  }, []);

  const startUpload = useCallback((uploads, taskId) => {
    dispatch({
      type: "START_UPLOAD",
      payload: { uploads, totalFiles: uploads.length, taskId },
    });
  }, []);

  const addXhrRequest = useCallback((taskId, xhr) => {
    dispatch({
      type: "ADD_XHR_REQUEST",
      payload: { taskId, xhr },
    });
  }, []);

  const removeXhrRequest = useCallback((taskId) => {
    dispatch({
      type: "REMOVE_XHR_REQUEST",
      payload: { taskId },
    });
  }, []);

  const updateUploadProgress = useCallback((id, progress) => {
    dispatch({
      type: "UPDATE_UPLOAD_PROGRESS",
      payload: { id, progress },
    });
  }, []);

  const updateUploadStatus = useCallback((id, status, error = null) => {
    dispatch({
      type: "UPDATE_UPLOAD_STATUS",
      payload: { id, status, error },
    });
  }, []);

  const cancelUpload = useCallback(() => {
    console.log("cancelUpload called");
    dispatch({ type: "CANCEL_UPLOAD" });
  }, []);

  const retryUpload = useCallback((uploadIds) => {
    dispatch({
      type: "RETRY_UPLOAD",
      payload: { ids: uploadIds },
    });
  }, []);

  const toggleMinimize = useCallback(() => {
    dispatch({ type: "TOGGLE_MINIMIZE" });
  }, []);

  const updatePosition = useCallback((position) => {
    dispatch({ type: "UPDATE_POSITION", payload: { position } });
  }, []);

  const clearUploads = useCallback(() => {
    dispatch({ type: "CLEAR_UPLOADS" });
  }, []);

  return (
    <UploadContext.Provider
      value={{
        ...state,
        setGeneratingUrls,
        startUpload,
        addXhrRequest,
        removeXhrRequest,
        updateUploadProgress,
        updateUploadStatus,
        cancelUpload,
        retryUpload,
        toggleMinimize,
        updatePosition,
        clearUploads,
      }}
    >
      {children}
    </UploadContext.Provider>
  );
};

export const useUpload = () => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error("useUpload must be used within an UploadProvider");
  }
  return context;
};
